const { EnumType } = require('json-to-graphql-query');
const moment = require('moment');
const { QueryBuilderAttributeTypes } = require('./operators');
const dateFormat = 'YYYY-MM-DD';

const formatDate = (date) => moment.utc(date).local().format(dateFormat);

/**
 * Populate the json-to-graphql-filter by translating the given query into criteria.
 * @param query The IQuery to be translated.
 * @param jsonToGraphQLFilter The json-to-graphql-filter to be updated in place.
 */
const addFilter = (query, jsonToGraphQLFilter) => {
    const criteriaGroups = [];

    // first create flat list of separate criteria groups
    query.where.forEach((it) => {
        if (!it.attribute || !it.operator || !it.value || !it.groupIds || !it.groupIds.length) {
            throw new Error('The query cannot be executed because the rule is invalid. You must clear the query and try again.');
        }

        // get criteria group for each group
        let parentCriteriaGroup = null;

        for (let i = 0; i < it.groupIds.length; i++) {
            const groupId = it.groupIds[i];
            const isLastGroup = i === it.groupIds.length - 1;
            const group = query.groups.find((g) => g.id === groupId);

            if (!group) {
                throw new Error(`The query cannot be executed because the ${it.attribute.label} rule has a missing group. You must delete the rule and try again.`);
            }

            let criteriaGroup = findCriteriaGroup(groupId, criteriaGroups);
            if (!criteriaGroup) {
                criteriaGroup = { // create new criteria group
                    criterias: [],
                    groupId,
                    condition: group.condition,
                    criteriaGroups: []
                };

                if (parentCriteriaGroup == null) {
                    criteriaGroups.push(criteriaGroup); // add to root (should only be one)
                } else if (!parentCriteriaGroup.criteriaGroups.find((cg) => cg.groupId === criteriaGroup.groupId)) {
                    parentCriteriaGroup.criteriaGroups.push(criteriaGroup); // add as child if not yet there
                }
            }

            if (isLastGroup) {
                const filterCriteria = {};
                addCriteria(it, filterCriteria);
                criteriaGroup.criterias.push(filterCriteria);
            }

            parentCriteriaGroup = criteriaGroup;
        }
    });

    // now build hierarchy of criteria groups
    if (criteriaGroups.length > 1) {
        throw new Error('The query cannot be executed because more than one root level group was found. You must clear the query and try again.');
    }

    if (criteriaGroups.length > 0) {
        addCriteriaGroup(criteriaGroups[0], jsonToGraphQLFilter);
    }
};

/**
 * Recursively find the pre-existing criteria group for the given groupId using the given list of 
 * criteria groups.
 * @param groupId The id of the group to locate.
 * @param criteriaGroups The list of criteria groups to search.
 * @returns The locted criteria group or null if not found.
 */
const findCriteriaGroup = (groupId, criteriaGroups) => {
    if (!criteriaGroups) {
        return null;
    }

    let criteriaGroup = criteriaGroups.find((cg) => cg.groupId === groupId);
    if (criteriaGroup) {
        return criteriaGroup;
    }

    for (const cg of criteriaGroups) {
        criteriaGroup = findCriteriaGroup(groupId, cg.criteriaGroups);
        if (criteriaGroup) {
            return criteriaGroup;
        }
    }

    return null;
};

/**
 * Recursively translate the given criteria group into an AND/OR group criteria and add it
 * to the filter.
 * @param criteriaGroup The criteria group to translate.
 * @param filter The existing filter object to be updated in place.
 */
const addCriteriaGroup = (criteriaGroup, filter) => {
    const filterCriteriaGroup = filter[criteriaGroup.condition.toUpperCase()] = [];
    for (let criteria of criteriaGroup.criterias) {
        filterCriteriaGroup.push(criteria);
    }
    for (let criteriaGroups of criteriaGroup.criteriaGroups) {
        const fcg = {};
        addCriteriaGroup(criteriaGroups, fcg);
        filterCriteriaGroup.push(fcg);
    }
};

/**
 * Translate the rule to criteria and add it to the filter.
 * @param rule The IRule to be translated.
 * @param filter The filterJson to be updated in place.
 */
const addCriteria = (rule, filter) => {
    const path = [...rule.attribute.path || []];
    if (rule.attribute.value) {
        path.push({ label: rule.attribute.label, value: rule.attribute.value })
    }
    let parent = filter;
    for (const pathItem of path) {
        parent = parent[pathItem.value] || (parent[pathItem.value] = {});
    }

    if (rule.attribute.type === QueryBuilderAttributeTypes.Lookup) {
        parent = parent['id'] || (parent['id'] = {});
    }

    parent[rule.operator] = getValue(rule);
};

/**
 * Return the value to be built into the filter object.
 * @param rule The rule for which to build the value.
 */
const getValue = (rule) => {
    let value = `${rule.value}`;

    switch (rule.attribute.type) {
        case QueryBuilderAttributeTypes.Int:
            value = Number(value);
            break;
        case QueryBuilderAttributeTypes.Enum:
            value = new EnumType(value);
            break;
        case QueryBuilderAttributeTypes.Lookup:
            value = (rule.value).id;
            break;
        case QueryBuilderAttributeTypes.Date:
            if (rule.operator === 'BETWEEN') { // TODO: make this dynamic when more than one example exists
                value = {
                    after: formatDate(rule.value),
                    before: formatDate(rule.valueB),
                };
            } else {
                value = formatDate(rule.value);
            }
            break;
    }

    return value;
};

module.exports = { addFilter };